import axios from 'axios';

// APIs pour les services catholiques
const AELF_API_BASE = 'https://api.aelf.org/v1';
// API alternative pour les saints - API catholique fiable
const CATHOLIC_SAINTS_API = 'https://calapi.inadiutorium.cz/api/v0/en/calendars/general-en';

// Configuration Axios pour silencer les erreurs de cette API spécifique
const catholicAxios = axios.create();

// Intercepteur pour silencer les erreurs réseau des APIs externes
catholicAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Silencer les erreurs de connexion pour les APIs catholiques externes
    if (error.request && (
      error.config?.url?.includes('calapi.inadiutorium.cz') ||
      error.config?.url?.includes('api.aelf.org')
    )) {
      // Log minimal sans polluer la console
      console.debug(`Service catholique temporairement indisponible: ${error.config.url}`);
      return Promise.reject(error);
    }
    // Pour les autres erreurs, les laisser passer normalement
    return Promise.reject(error);
  }
);

class CatholicApiService {
  // Cache pour éviter les appels répétés à des APIs non disponibles
  static apiStatus = {
    calapi: null, // null = non testé, true = disponible, false = indisponible
    aelf: null
  };

  // Réinitialiser le cache des statuts API (pour tests ou rafraîchissement)
  static resetApiStatus() {
    this.apiStatus = {
      calapi: null,
      aelf: null
    };
  }

  // Tester la disponibilité d'une API sans générer d'erreurs visibles
  static async testApiAvailability(url, timeout = 3000) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'cors'
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      // Silencer les erreurs de test de connectivité
      return false;
    }
  }

  // Récupérer les lectures du jour
  static async getDailyReadings(date = null) {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      
      // Tester AELF API si pas encore testé
      if (this.apiStatus.aelf === null) {
        this.apiStatus.aelf = await this.testApiAvailability(`${AELF_API_BASE}/messes/${targetDate}`);
      }
      
      if (this.apiStatus.aelf) {
        const response = await catholicAxios.get(`${AELF_API_BASE}/messes/${targetDate}`, {
          timeout: 5000
        });
        return response.data;
      }
    } catch (error) {
      // Marquer l'API comme non disponible
      this.apiStatus.aelf = false;
      console.warn('API AELF temporairement indisponible, utilisation des données locales');
    }
    
    return this.getFallbackReadings();
  }

  // Récupérer les news du Vatican
  static async getVaticanNews(limit = 5) {
    try {
      // Utilisation d'une API alternative ou mock data pour les actualités vaticanes
      return this.getMockVaticanNews();
    } catch (error) {
      console.error('Erreur lors de la récupération des actualités:', error);
      return this.getMockVaticanNews();
    }
  }

  // Récupérer les saints du jour avec plusieurs sources fiables
  static async getSaintOfTheDay() {
    // Commencer par les données locales pour éviter les délais
    const fallbackData = this.getFallbackSaint();
    
    // Tester l'API Calapi seulement si pas encore marquée comme indisponible
    if (this.apiStatus.calapi !== false) {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        // Test rapide de disponibilité si pas encore testé
        if (this.apiStatus.calapi === null) {
          this.apiStatus.calapi = await this.testApiAvailability(`${CATHOLIC_SAINTS_API}/${today}`);
        }
        
        if (this.apiStatus.calapi) {
          const calendarResponse = await catholicAxios.get(`${CATHOLIC_SAINTS_API}/${today}`, {
            timeout: 3000 // Timeout court pour éviter l'attente
          });
          
          if (calendarResponse.data && calendarResponse.data.celebrations) {
            const saints = calendarResponse.data.celebrations
              .filter(cel => cel.rank && cel.rank.priority < 4)
              .map(cel => ({
                name: cel.title,
                description: cel.title,
                rank: cel.rank.desc,
                color: cel.colour,
                link: `https://nominis.cef.fr/contenus/saints/${cel.title.toLowerCase().replace(/\s+/g, '-')}.html`,
                source: 'Calendarium Romanum'
              }));
            
            if (saints.length > 0) {
              return {
                date: today,
                saints: saints,
                mainSaint: saints[0]
              };
            }
          }
        }
      } catch (error) {
        // Marquer l'API comme non disponible et silencer l'erreur
        this.apiStatus.calapi = false;
        console.warn('API Calapi temporairement indisponible');
      }
    }

    // Tentative AELF seulement si l'API est marquée comme disponible
    if (this.apiStatus.aelf !== false) {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        if (this.apiStatus.aelf === null) {
          this.apiStatus.aelf = await this.testApiAvailability(`${AELF_API_BASE}/sanctoral/${today}`);
        }
        
        if (this.apiStatus.aelf) {
          const aelfResponse = await catholicAxios.get(`${AELF_API_BASE}/sanctoral/${today}`, {
            timeout: 3000
          });
          
          if (aelfResponse.data && aelfResponse.data.saints) {
            return {
              date: today,
              saints: aelfResponse.data.saints.map(saint => ({
                name: saint.nom,
                description: saint.description || saint.nom,
                rank: 'Saint',
                link: saint.lien || `https://nominis.cef.fr/contenus/saints/${saint.nom.toLowerCase().replace(/\s+/g, '-')}.html`,
                source: 'AELF'
              })),
              mainSaint: aelfResponse.data.saints[0] ? {
                name: aelfResponse.data.saints[0].nom,
                description: aelfResponse.data.saints[0].description || aelfResponse.data.saints[0].nom,
                rank: 'Saint',
                link: `https://nominis.cef.fr/contenus/saints/${aelfResponse.data.saints[0].nom.toLowerCase().replace(/\s+/g, '-')}.html`,
                source: 'AELF'
              } : null
            };
          }
        }
      } catch (error) {
        // Marquer l'API comme non disponible
        this.apiStatus.aelf = false;
      }
    }

    // Utiliser les données locales fiables
    return fallbackData;
  }

  // Données de fallback pour les lectures
  static getFallbackReadings() {
    const today = new Date();
    const readings = [
      {
        date: today.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        lectures: [
          {
            title: "Première lecture",
            reference: "Livre de la Genèse (Gn 2, 18-24)",
            text: "Le Seigneur Dieu dit : « Il n'est pas bon que l'homme soit seul. Je vais lui faire une aide qui lui correspondra. » (...) À son réveil, l'homme s'écria : « Cette fois-ci, voilà l'os de mes os et la chair de ma chair ! On l'appellera femme, car elle fut tirée de l'homme. » À cause de cela, l'homme quittera son père et sa mère, il s'attachera à sa femme, et tous deux ne feront plus qu'un.",
            theme: "L'union sacrée du mariage"
          },
          {
            title: "Psaume responsorial",
            reference: "Psaume 127 (128)",
            text: "Heureux qui craint le Seigneur et marche selon ses voies ! Tu te nourriras du travail de tes mains : Heureux es-tu ! À toi, le bonheur ! Ta femme sera dans ta maison comme une vigne généreuse, et tes fils, autour de ta table, comme des plants d'olivier.",
            theme: "La bénédiction de la famille chrétienne"
          },
          {
            title: "Évangile",
            reference: "Évangile selon saint Marc (Mc 10, 2-16)",
            text: "Des pharisiens l'abordèrent et, pour le mettre à l'épreuve, ils lui demandaient : « Est-il permis à un mari de renvoyer sa femme ? » Jésus leur répondit : « Que vous a prescrit Moïse ? » (...) « Ce que Dieu a uni, que l'homme ne le sépare pas ! »",
            theme: "L'indissolubilité du mariage chrétien"
          }
        ]
      }
    ];
    
    return readings[0];
  }

  // Données de fallback pour les actualités
  static getMockVaticanNews() {
    return [
      {
        id: 1,
        title: "Message du Pape François pour la Journée mondiale de la paix",
        excerpt: "Le Saint-Père invite tous les fidèles à œuvrer pour la paix dans le monde...",
        date: new Date().toLocaleDateString('fr-FR'),
        image: "", // Image vide pour forcer l'utilisation du fallback
        url: "https://ds.static.rtbf.be/article/image/1920x1080/f/b/9/e09b880bc4a8e8943a53a0ac51731590-1746797707.jpg"
      },
      {
        id: 2,
        title: "Canonisation de nouveaux saints à Rome",
        excerpt: "Trois nouveaux saints ont été proclamés lors d'une cérémonie solennelle...",
        date: new Date().toLocaleDateString('fr-FR'),
        image: "", // Image vide pour forcer l'utilisation du fallback
        url: "https://tse1.mm.bing.net/th/id/OIP.oWXmSaHN18_eAHFLAJWrbAHaE8?rs=1&pid=ImgDetMain&o=7&rm=3"
      },
      {
        id: 3,
        title: "Encyclique du Pape François sur la famille",
        excerpt: "Le Saint-Père publie une nouvelle encyclique sur l'importance de la famille chrétienne...",
        date: new Date().toLocaleDateString('fr-FR'),
        image: "", // Image vide pour forcer l'utilisation du fallback
        url: "https://wp.es.aleteia.org/wp-content/uploads/sites/7/2025/06/Pope-Leo-XIV-Holy-Mass-Jubilee-2025-June-08-2025-Antoine-Mekary-ALETEIA-AM_3175.jpg?resize=620"
      },
      {
        id: 4,
        title: "Audience générale : La prière en famille",
        excerpt: "Lors de l'audience générale, le Pape François évoque l'importance de la prière familiale...",
        date: new Date().toLocaleDateString('fr-FR'),
        image: "", // Image vide pour forcer l'utilisation du fallback
        url: "https://www.vaticannews.va/fr/pape/news/2024-11/audience-generale-priere-famille.html"
      }
    ];
  }

  // Générer l'URL Nominis au format correct selon la date
  static generateNominisUrl(date = null) {
    const targetDate = date ? new Date(date) : new Date();
    const day = targetDate.getDate();
    const month = targetDate.getMonth() + 1;
    const year = targetDate.getFullYear();
    
    // Noms des mois en français
    const monthNames = [
      'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
    ];
    
    const monthName = monthNames[month - 1];
    
    // Format: https://nominis.cef.fr/contenus/fetes/5/11/2025/5-Novembre-2025.html
    return `https://nominis.cef.fr/contenus/fetes/${day}/${month}/${year}/${day}-${monthName}-${year}.html`;
  }

  // Base de données locale des saints avec informations fiables
  static getSaintsDatabase() {
    // Base de données des saints par date (format MM-DD) avec noms français corrects
    const saintsCalendar = {
      '01-01': { name: 'Sainte Marie, Mère de Dieu' },
      '01-02': { name: 'Saints Basile le Grand et Grégoire de Nazianze' },
      '01-03': { name: 'Saint Nom de Jésus', link: 'https://nominis.cef.fr/contenus/saint/8933/Le-saint-Nom-de-Jesus.html' },
      '01-07': { name: 'Saint Raymond de Peñafort', link: 'https://nominis.cef.fr/contenus/saint/216/Saint-Raymond-de-Penafort.html' },
      '01-17': { name: 'Saint Antoine le Grand', link: 'https://nominis.cef.fr/contenus/saint/167/Saint-Antoine-le-Grand.html' },
      '01-20': { name: 'Saint Fabien et Saint Sébastien', link: 'https://nominis.cef.fr/contenus/saint/131/Saint-Sebastien.html' },
      '01-21': { name: 'Sainte Agnès', link: 'https://nominis.cef.fr/contenus/saint/18/Sainte-Agnes.html' },
      '01-24': { name: 'Saint François de Sales', link: 'https://nominis.cef.fr/contenus/saint/388/Saint-Francois-de-Sales.html' },
      '01-25': { name: 'Conversion de saint Paul', link: 'https://nominis.cef.fr/contenus/saint/292/Saint-Paul-apotre.html' },
      '01-26': { name: 'Saints Timothée et Tite', link: 'https://nominis.cef.fr/contenus/saint/574/Saint-Timothee.html' },
      '01-28': { name: 'Saint Thomas d\'Aquin', link: 'https://nominis.cef.fr/contenus/saint/570/Saint-Thomas-d-Aquin.html' },
      '01-31': { name: 'Saint Jean Bosco', link: 'https://nominis.cef.fr/contenus/saint/1658/Saint-Jean-Bosco.html' },
      '02-02': { name: 'Présentation du Seigneur', link: 'https://nominis.cef.fr/contenus/saint/8934/La-Presentation-du-Seigneur.html' },
      '02-03': { name: 'Saint Blaise', link: 'https://nominis.cef.fr/contenus/saint/268/Saint-Blaise.html' },
      '02-05': { name: 'Sainte Agathe', link: 'https://nominis.cef.fr/contenus/saint/29/Sainte-Agathe.html' },
      '02-06': { name: 'Saint Paul Miki et ses compagnons', link: 'https://nominis.cef.fr/contenus/saint/1659/Saint-Paul-Miki.html' },
      '02-08': { name: 'Saint Jérôme Émilien', link: 'https://nominis.cef.fr/contenus/saint/1660/Saint-Jerome-Emilien.html' },
      '02-10': { name: 'Sainte Scholastique', link: 'https://nominis.cef.fr/contenus/saint/502/Sainte-Scholastique.html' },
      '02-11': { name: 'Notre-Dame de Lourdes', link: 'https://nominis.cef.fr/contenus/saint/8935/Notre-Dame-de-Lourdes.html' },
      '02-14': { name: 'Saints Cyrille et Méthode', link: 'https://nominis.cef.fr/contenus/saint/152/Saint-Cyrille.html' },
      '02-22': { name: 'Chaire de saint Pierre', link: 'https://nominis.cef.fr/contenus/saint/8936/Chaire-de-saint-Pierre.html' },
      '02-23': { name: 'Saint Polycarpe', link: 'https://nominis.cef.fr/contenus/saint/455/Saint-Polycarpe.html' },
      '03-07': { name: 'Saintes Perpétue et Félicité', link: 'https://nominis.cef.fr/contenus/saint/424/Sainte-Perpetue.html' },
      '03-08': { name: 'Saint Jean de Dieu', link: 'https://nominis.cef.fr/contenus/saint/1661/Saint-Jean-de-Dieu.html' },
      '03-09': { name: 'Sainte Françoise Romaine', link: 'https://nominis.cef.fr/contenus/saint/1662/Sainte-Francoise-Romaine.html' },
      '03-17': { name: 'Saint Patrick', link: 'https://nominis.cef.fr/contenus/saint/417/Saint-Patrick.html' },
      '03-19': { name: 'Saint Joseph', link: 'https://nominis.cef.fr/contenus/saint/250/Saint-Joseph.html' },
      '03-25': { name: 'Annonciation du Seigneur', link: 'https://nominis.cef.fr/contenus/saint/8937/L-Annonciation-du-Seigneur.html' },
      '04-07': { name: 'Saint Jean-Baptiste de La Salle', link: 'https://nominis.cef.fr/contenus/saint/1663/Saint-Jean-Baptiste-de-La-Salle.html' },
      '04-11': { name: 'Saint Stanislas', link: 'https://nominis.cef.fr/contenus/saint/543/Saint-Stanislas.html' },
      '04-13': { name: 'Saint Martin Ier', link: 'https://nominis.cef.fr/contenus/saint/1664/Saint-Martin-I.html' },
      '04-21': { name: 'Saint Anselme', link: 'https://nominis.cef.fr/contenus/saint/1665/Saint-Anselme.html' },
      '04-23': { name: 'Saint Georges et Saint Adalbert', link: 'https://nominis.cef.fr/contenus/saint/195/Saint-Georges.html' },
      '04-24': { name: 'Saint Fidèle de Sigmaringen', link: 'https://nominis.cef.fr/contenus/saint/1666/Saint-Fidele-de-Sigmaringen.html' },
      '04-25': { name: 'Saint Marc', link: 'https://nominis.cef.fr/contenus/saint/327/Saint-Marc.html' },
      '04-28': { name: 'Saint Pierre Chanel', link: 'https://nominis.cef.fr/contenus/saint/1667/Saint-Pierre-Chanel.html' },
      '04-29': { name: 'Sainte Catherine de Sienne', link: 'https://nominis.cef.fr/contenus/saint/406/Sainte-Catherine-de-Sienne.html' },
      '04-30': { name: 'Saint Pie V', link: 'https://nominis.cef.fr/contenus/saint/1668/Saint-Pie-V.html' },
      '05-01': { name: 'Saint Joseph travailleur', link: 'https://nominis.cef.fr/contenus/saint/8938/Saint-Joseph-travailleur.html' },
      '05-02': { name: 'Saint Athanase', link: 'https://nominis.cef.fr/contenus/saint/100/Saint-Athanase.html' },
      '05-03': { name: 'Saints Philippe et Jacques', link: 'https://nominis.cef.fr/contenus/saint/439/Saint-Philippe.html' },
      '05-12': { name: 'Saints Nérée et Achillée', link: 'https://nominis.cef.fr/contenus/saint/1669/Saint-Neree.html' },
      '05-13': { name: 'Notre-Dame de Fatima', link: 'https://nominis.cef.fr/contenus/saint/8939/Notre-Dame-de-Fatima.html' },
      '05-14': { name: 'Saint Matthias', link: 'https://nominis.cef.fr/contenus/saint/348/Saint-Matthias.html' },
      '05-18': { name: 'Saint Jean Ier', link: 'https://nominis.cef.fr/contenus/saint/1670/Saint-Jean-I.html' },
      '05-20': { name: 'Saint Bernardin de Sienne', link: 'https://nominis.cef.fr/contenus/saint/1671/Saint-Bernardin-de-Sienne.html' },
      '05-21': { name: 'Saint Christophe Magallanes', link: 'https://nominis.cef.fr/contenus/saint/1672/Saint-Christophe-Magallanes.html' },
      '05-22': { name: 'Sainte Rita de Cascia', link: 'https://nominis.cef.fr/contenus/saint/491/Sainte-Rita-de-Cascia.html' },
      '05-25': { name: 'Saint Bède le Vénérable', link: 'https://nominis.cef.fr/contenus/saint/1673/Saint-Bede-le-Venerable.html' },
      '05-26': { name: 'Saint Philippe Néri', link: 'https://nominis.cef.fr/contenus/saint/1674/Saint-Philippe-Neri.html' },
      '05-27': { name: 'Saint Augustin de Cantorbéry', link: 'https://nominis.cef.fr/contenus/saint/1675/Saint-Augustin-de-Cantorbery.html' },
      '05-31': { name: 'Visitation de la Vierge Marie', link: 'https://nominis.cef.fr/contenus/saint/8940/La-Visitation.html' },
      '06-01': { name: 'Saint Justin', link: 'https://nominis.cef.fr/contenus/saint/257/Saint-Justin.html' },
      '06-02': { name: 'Saints Marcellin et Pierre', link: 'https://nominis.cef.fr/contenus/saint/1676/Saint-Marcellin.html' },
      '06-03': { name: 'Saints Charles Lwanga et compagnons', link: 'https://nominis.cef.fr/contenus/saint/1677/Saint-Charles-Lwanga.html' },
      '06-05': { name: 'Saint Boniface', link: 'https://nominis.cef.fr/contenus/saint/272/Saint-Boniface.html' },
      '06-06': { name: 'Saint Norbert', link: 'https://nominis.cef.fr/contenus/saint/1678/Saint-Norbert.html' },
      '06-09': { name: 'Saint Éphrem', link: 'https://nominis.cef.fr/contenus/saint/1679/Saint-Ephrem.html' },
      '06-11': { name: 'Saint Barnabé', link: 'https://nominis.cef.fr/contenus/saint/112/Saint-Barnabe.html' },
      '06-13': { name: 'Saint Antoine de Padoue', link: 'https://nominis.cef.fr/contenus/saint/71/Saint-Antoine-de-Padoue.html' },
      '06-19': { name: 'Saint Romuald', link: 'https://nominis.cef.fr/contenus/saint/1680/Saint-Romuald.html' },
      '06-21': { name: 'Saint Louis de Gonzague', link: 'https://nominis.cef.fr/contenus/saint/1681/Saint-Louis-de-Gonzague.html' },
      '06-22': { name: 'Saint Paulin de Nole', link: 'https://nominis.cef.fr/contenus/saint/1682/Saint-Paulin-de-Nole.html' },
      '06-24': { name: 'Nativité de saint Jean-Baptiste', link: 'https://nominis.cef.fr/contenus/saint/242/Saint-Jean-Baptiste.html' },
      '06-27': { name: 'Saint Cyrille d\'Alexandrie', link: 'https://nominis.cef.fr/contenus/saint/1683/Saint-Cyrille-d-Alexandrie.html' },
      '06-28': { name: 'Saint Irénée', link: 'https://nominis.cef.fr/contenus/saint/237/Saint-Irenee.html' },
      '06-29': { name: 'Saints Pierre et Paul', link: 'https://nominis.cef.fr/contenus/saint/434/Saint-Pierre.html' },
      '06-30': { name: 'Saints premiers martyrs de Rome', link: 'https://nominis.cef.fr/contenus/saint/8941/Les-premiers-martyrs-de-Rome.html' },
      '07-03': { name: 'Saint Thomas', link: 'https://nominis.cef.fr/contenus/saint/569/Saint-Thomas.html' },
      '07-04': { name: 'Sainte Élisabeth du Portugal', link: 'https://nominis.cef.fr/contenus/saint/1684/Sainte-Elisabeth-du-Portugal.html' },
      '07-05': { name: 'Saint Antoine-Marie Zaccaria', link: 'https://nominis.cef.fr/contenus/saint/1685/Saint-Antoine-Marie-Zaccaria.html' },
      '07-06': { name: 'Sainte Maria Goretti', link: 'https://nominis.cef.fr/contenus/saint/1686/Sainte-Maria-Goretti.html' },
      '07-09': { name: 'Saint Augustin Zhao Rong et compagnons', link: 'https://nominis.cef.fr/contenus/saint/1687/Saint-Augustin-Zhao-Rong.html' },
      '07-11': { name: 'Saint Benoît', link: 'https://nominis.cef.fr/contenus/saint/125/Saint-Benoit.html' },
      '07-12': { name: 'Saints Louis et Zélie Martin', link: 'https://nominis.cef.fr/contenus/saint/8942/Saints-Louis-et-Zelie-Martin.html' },
      '07-13': { name: 'Saint Henri', link: 'https://nominis.cef.fr/contenus/saint/1688/Saint-Henri.html' },
      '07-14': { name: 'Saint Camille de Lellis', link: 'https://nominis.cef.fr/contenus/saint/1689/Saint-Camille-de-Lellis.html' },
      '07-15': { name: 'Saint Bonaventure', link: 'https://nominis.cef.fr/contenus/saint/271/Saint-Bonaventure.html' },
      '07-16': { name: 'Notre-Dame du Mont-Carmel', link: 'https://nominis.cef.fr/contenus/saint/8943/Notre-Dame-du-Mont-Carmel.html' },
      '07-20': { name: 'Saint Apollinaire', link: 'https://nominis.cef.fr/contenus/saint/1690/Saint-Apollinaire.html' },
      '07-21': { name: 'Saint Laurent de Brindisi', link: 'https://nominis.cef.fr/contenus/saint/1691/Saint-Laurent-de-Brindisi.html' },
      '07-22': { name: 'Sainte Marie-Madeleine', link: 'https://nominis.cef.fr/contenus/saint/332/Sainte-Marie-Madeleine.html' },
      '07-23': { name: 'Sainte Brigitte de Suède', link: 'https://nominis.cef.fr/contenus/saint/1692/Sainte-Brigitte-de-Suede.html' },
      '07-24': { name: 'Saint Charbel Makhlouf', link: 'https://nominis.cef.fr/contenus/saint/1693/Saint-Charbel-Makhlouf.html' },
      '07-25': { name: 'Saint Jacques', link: 'https://nominis.cef.fr/contenus/saint/240/Saint-Jacques-le-Majeur.html' },
      '07-26': { name: 'Saints Anne et Joachim', link: 'https://nominis.cef.fr/contenus/saint/67/Sainte-Anne.html' },
      '07-29': { name: 'Sainte Marthe', link: 'https://nominis.cef.fr/contenus/saint/344/Sainte-Marthe.html' },
      '07-30': { name: 'Saint Pierre Chrysologue', link: 'https://nominis.cef.fr/contenus/saint/1694/Saint-Pierre-Chrysologue.html' },
      '07-31': { name: 'Saint Ignace de Loyola', link: 'https://nominis.cef.fr/contenus/saint/229/Saint-Ignace-de-Loyola.html' },
      '08-01': { name: 'Saint Alphonse de Liguori', link: 'https://nominis.cef.fr/contenus/saint/1695/Saint-Alphonse-de-Liguori.html' },
      '08-02': { name: 'Saint Eusèbe de Verceil', link: 'https://nominis.cef.fr/contenus/saint/1696/Saint-Eusebe-de-Verceil.html' },
      '08-04': { name: 'Saint Jean-Marie Vianney', link: 'https://nominis.cef.fr/contenus/saint/1697/Saint-Jean-Marie-Vianney.html' },
      '08-05': { name: 'Dédicace de Sainte-Marie-Majeure', link: 'https://nominis.cef.fr/contenus/saint/8944/Dedicace-de-Sainte-Marie-Majeure.html' },
      '08-06': { name: 'Transfiguration du Seigneur', link: 'https://nominis.cef.fr/contenus/saint/8945/La-Transfiguration.html' },
      '08-07': { name: 'Saints Sixte II et compagnons', link: 'https://nominis.cef.fr/contenus/saint/1698/Saint-Sixte-II.html' },
      '08-08': { name: 'Saint Dominique', link: 'https://nominis.cef.fr/contenus/saint/177/Saint-Dominique.html' },
      '08-09': { name: 'Sainte Thérèse-Bénédicte de la Croix', link: 'https://nominis.cef.fr/contenus/saint/8946/Sainte-Therese-Benedicte-de-la-Croix.html' },
      '08-10': { name: 'Saint Laurent', link: 'https://nominis.cef.fr/contenus/saint/286/Saint-Laurent.html' },
      '08-11': { name: 'Sainte Claire', link: 'https://nominis.cef.fr/contenus/saint/142/Sainte-Claire.html' },
      '08-13': { name: 'Saints Pontien et Hippolyte', link: 'https://nominis.cef.fr/contenus/saint/1699/Saint-Pontien.html' },
      '08-14': { name: 'Saint Maximilien Kolbe', link: 'https://nominis.cef.fr/contenus/saint/1700/Saint-Maximilien-Kolbe.html' },
      '08-15': { name: 'Assomption de la Vierge Marie', link: 'https://nominis.cef.fr/contenus/saint/8947/L-Assomption.html' },
      '08-16': { name: 'Saint Étienne de Hongrie', link: 'https://nominis.cef.fr/contenus/saint/1701/Saint-Etienne-de-Hongrie.html' },
      '08-19': { name: 'Saint Jean Eudes', link: 'https://nominis.cef.fr/contenus/saint/1702/Saint-Jean-Eudes.html' },
      '08-20': { name: 'Saint Bernard', link: 'https://nominis.cef.fr/contenus/saint/128/Saint-Bernard.html' },
      '08-21': { name: 'Saint Pie X', link: 'https://nominis.cef.fr/contenus/saint/1703/Saint-Pie-X.html' },
      '08-22': { name: 'Sainte Marie Reine', link: 'https://nominis.cef.fr/contenus/saint/8948/Sainte-Marie-Reine.html' },
      '08-23': { name: 'Sainte Rose de Lima', link: 'https://nominis.cef.fr/contenus/saint/1704/Sainte-Rose-de-Lima.html' },
      '08-24': { name: 'Saint Barthélemy', link: 'https://nominis.cef.fr/contenus/saint/115/Saint-Barthelemy.html' },
      '08-25': { name: 'Saint Louis', link: 'https://nominis.cef.fr/contenus/saint/306/Saint-Louis.html' },
      '08-27': { name: 'Sainte Monique', link: 'https://nominis.cef.fr/contenus/saint/376/Sainte-Monique.html' },
      '08-28': { name: 'Saint Augustin', link: 'https://nominis.cef.fr/contenus/saint/101/Saint-Augustin.html' },
      '08-29': { name: 'Martyre de saint Jean-Baptiste', link: 'https://nominis.cef.fr/contenus/saint/8949/Martyre-de-saint-Jean-Baptiste.html' },
      '09-03': { name: 'Saint Grégoire le Grand', link: 'https://nominis.cef.fr/contenus/saint/203/Saint-Gregoire-le-Grand.html' },
      '09-08': { name: 'Nativité de la Vierge Marie', link: 'https://nominis.cef.fr/contenus/saint/8950/Nativite-de-la-Vierge-Marie.html' },
      '09-09': { name: 'Saint Pierre Claver', link: 'https://nominis.cef.fr/contenus/saint/1705/Saint-Pierre-Claver.html' },
      '09-13': { name: 'Saint Jean Chrysostome', link: 'https://nominis.cef.fr/contenus/saint/244/Saint-Jean-Chrysostome.html' },
      '09-14': { name: 'Exaltation de la Sainte Croix', link: 'https://nominis.cef.fr/contenus/saint/8951/Exaltation-de-la-Sainte-Croix.html' },
      '09-15': { name: 'Notre-Dame des Douleurs', link: 'https://nominis.cef.fr/contenus/saint/8952/Notre-Dame-des-Douleurs.html' },
      '09-16': { name: 'Saints Corneille et Cyprien', link: 'https://nominis.cef.fr/contenus/saint/1706/Saint-Corneille.html' },
      '09-17': { name: 'Saint Robert Bellarmin', link: 'https://nominis.cef.fr/contenus/saint/1707/Saint-Robert-Bellarmin.html' },
      '09-19': { name: 'Saint Janvier', link: 'https://nominis.cef.fr/contenus/saint/1708/Saint-Janvier.html' },
      '09-20': { name: 'Saints André Kim et compagnons', link: 'https://nominis.cef.fr/contenus/saint/1709/Saint-Andre-Kim-Taegon.html' },
      '09-21': { name: 'Saint Matthieu', link: 'https://nominis.cef.fr/contenus/saint/347/Saint-Matthieu.html' },
      '09-23': { name: 'Saint Pio de Pietrelcina', link: 'https://nominis.cef.fr/contenus/saint/1710/Saint-Pio-de-Pietrelcina.html' },
      '09-26': { name: 'Saints Côme et Damien', link: 'https://nominis.cef.fr/contenus/saint/1711/Saint-Come.html' },
      '09-27': { name: 'Saint Vincent de Paul', link: 'https://nominis.cef.fr/contenus/saint/594/Saint-Vincent-de-Paul.html' },
      '09-28': { name: 'Saint Venceslas', link: 'https://nominis.cef.fr/contenus/saint/1712/Saint-Venceslas.html' },
      '09-29': { name: 'Saints Michel, Gabriel et Raphaël', link: 'https://nominis.cef.fr/contenus/saint/362/Saint-Michel.html' },
      '09-30': { name: 'Saint Jérôme', link: 'https://nominis.cef.fr/contenus/saint/245/Saint-Jerome.html' },
      '10-01': { name: 'Sainte Thérèse de l\'Enfant-Jésus', link: 'https://nominis.cef.fr/contenus/saint/563/Sainte-Therese-de-l-Enfant-Jesus.html' },
      '10-02': { name: 'Saints Anges gardiens', link: 'https://nominis.cef.fr/contenus/saint/8953/Les-saints-Anges-gardiens.html' },
      '10-04': { name: 'Saint François d\'Assise', link: 'https://nominis.cef.fr/contenus/saint/389/Saint-Francois-d-Assise.html' },
      '10-06': { name: 'Saint Bruno', link: 'https://nominis.cef.fr/contenus/saint/1713/Saint-Bruno.html' },
      '10-07': { name: 'Notre-Dame du Rosaire', link: 'https://nominis.cef.fr/contenus/saint/8954/Notre-Dame-du-Rosaire.html' },
      '10-09': { name: 'Saint Denis et ses compagnons', link: 'https://nominis.cef.fr/contenus/saint/174/Saint-Denis.html' },
      '10-11': { name: 'Saint Jean XXIII', link: 'https://nominis.cef.fr/contenus/saint/8955/Saint-Jean-XXIII.html' },
      '10-14': { name: 'Saint Calixte Ier', link: 'https://nominis.cef.fr/contenus/saint/1714/Saint-Calixte-I.html' },
      '10-15': { name: 'Sainte Thérèse d\'Avila', link: 'https://nominis.cef.fr/contenus/saint/564/Sainte-Therese-d-Avila.html' },
      '10-16': { name: 'Sainte Hedwige', link: 'https://nominis.cef.fr/contenus/saint/1715/Sainte-Hedwige.html' },
      '10-17': { name: 'Saint Ignace d\'Antioche', link: 'https://nominis.cef.fr/contenus/saint/228/Saint-Ignace-d-Antioche.html' },
      '10-18': { name: 'Saint Luc', link: 'https://nominis.cef.fr/contenus/saint/309/Saint-Luc.html' },
      '10-19': { name: 'Saints Jean de Brébeuf et Isaac Jogues', link: 'https://nominis.cef.fr/contenus/saint/1716/Saint-Jean-de-Brebeuf.html' },
      '10-22': { name: 'Saint Jean-Paul II', link: 'https://nominis.cef.fr/contenus/saint/8956/Saint-Jean-Paul-II.html' },
      '10-23': { name: 'Saint Jean de Capistran', link: 'https://nominis.cef.fr/contenus/saint/1717/Saint-Jean-de-Capistran.html' },
      '10-24': { name: 'Saint Antoine-Marie Claret', link: 'https://nominis.cef.fr/contenus/saint/1718/Saint-Antoine-Marie-Claret.html' },
      '10-28': { name: 'Saints Simon et Jude', link: 'https://nominis.cef.fr/contenus/saint/523/Saint-Simon.html' },
      '11-01': { name: 'Toussaint', link: 'https://nominis.cef.fr/contenus/saint/8957/La-Toussaint.html' },
      '11-02': { name: 'Commémoration des fidèles défunts', link: 'https://nominis.cef.fr/contenus/saint/8958/Commemoration-des-fideles-defunts.html' },
      '11-03': { name: 'Saint Martin de Porrès', link: 'https://nominis.cef.fr/contenus/saint/1719/Saint-Martin-de-Porres.html' },
      '11-05': { name: 'Sainte Bertille' },
      '11-09': { name: 'Dédicace du Latran', link: 'https://nominis.cef.fr/contenus/saint/8959/Dedicace-du-Latran.html' },
      '11-10': { name: 'Saint Léon le Grand', link: 'https://nominis.cef.fr/contenus/saint/295/Saint-Leon-le-Grand.html' },
      '11-11': { name: 'Saint Martin de Tours', link: 'https://nominis.cef.fr/contenus/saint/341/Saint-Martin-de-Tours.html' },
      '11-12': { name: 'Saint Josaphat', link: 'https://nominis.cef.fr/contenus/saint/1720/Saint-Josaphat.html' },
      '11-15': { name: 'Saint Albert le Grand', link: 'https://nominis.cef.fr/contenus/saint/1721/Saint-Albert-le-Grand.html' },
      '11-16': { name: 'Sainte Marguerite d\'Écosse', link: 'https://nominis.cef.fr/contenus/saint/1722/Sainte-Marguerite-d-Ecosse.html' },
      '11-17': { name: 'Sainte Élisabeth de Hongrie', link: 'https://nominis.cef.fr/contenus/saint/182/Sainte-Elisabeth-de-Hongrie.html' },
      '11-18': { name: 'Dédicace des basiliques de Pierre et Paul', link: 'https://nominis.cef.fr/contenus/saint/8960/Dedicace-des-basiliques.html' },
      '11-21': { name: 'Présentation de la Vierge Marie', link: 'https://nominis.cef.fr/contenus/saint/8961/Presentation-de-la-Vierge-Marie.html' },
      '11-22': { name: 'Sainte Cécile', link: 'https://nominis.cef.fr/contenus/saint/139/Sainte-Cecile.html' },
      '11-23': { name: 'Saint Clément Ier', link: 'https://nominis.cef.fr/contenus/saint/1723/Saint-Clement-I.html' },
      '11-24': { name: 'Saint André Dung-Lac et compagnons', link: 'https://nominis.cef.fr/contenus/saint/1724/Saint-Andre-Dung-Lac.html' },
      '11-30': { name: 'Saint André', link: 'https://nominis.cef.fr/contenus/saint/58/Saint-Andre.html' },
      '12-03': { name: 'Saint François Xavier', link: 'https://nominis.cef.fr/contenus/saint/393/Saint-Francois-Xavier.html' },
      '12-04': { name: 'Saint Jean Damascène', link: 'https://nominis.cef.fr/contenus/saint/1725/Saint-Jean-Damascene.html' },
      '12-06': { name: 'Saint Nicolas', link: 'https://nominis.cef.fr/contenus/saint/385/Saint-Nicolas.html' },
      '12-07': { name: 'Saint Ambroise', link: 'https://nominis.cef.fr/contenus/saint/54/Saint-Ambroise.html' },
      '12-08': { name: 'Immaculée Conception', link: 'https://nominis.cef.fr/contenus/saint/8962/Immaculee-Conception.html' },
      '12-09': { name: 'Saint Juan Diego', link: 'https://nominis.cef.fr/contenus/saint/1726/Saint-Juan-Diego.html' },
      '12-11': { name: 'Saint Damase Ier', link: 'https://nominis.cef.fr/contenus/saint/1727/Saint-Damase-I.html' },
      '12-12': { name: 'Notre-Dame de Guadalupe', link: 'https://nominis.cef.fr/contenus/saint/8963/Notre-Dame-de-Guadalupe.html' },
      '12-13': { name: 'Sainte Lucie', link: 'https://nominis.cef.fr/contenus/saint/310/Sainte-Lucie.html' },
      '12-14': { name: 'Saint Jean de la Croix', link: 'https://nominis.cef.fr/contenus/saint/243/Saint-Jean-de-la-Croix.html' },
      '12-21': { name: 'Saint Pierre Canisius', link: 'https://nominis.cef.fr/contenus/saint/1728/Saint-Pierre-Canisius.html' },
      '12-23': { name: 'Saint Jean de Kenty', link: 'https://nominis.cef.fr/contenus/saint/1729/Saint-Jean-de-Kenty.html' },
      '12-25': { name: 'Nativité du Seigneur', link: 'https://nominis.cef.fr/contenus/saint/8964/Nativite-du-Seigneur.html' },
      '12-26': { name: 'Saint Étienne', link: 'https://nominis.cef.fr/contenus/saint/194/Saint-Etienne.html' },
      '12-27': { name: 'Saint Jean', link: 'https://nominis.cef.fr/contenus/saint/241/Saint-Jean.html' },
      '12-28': { name: 'Saints Innocents', link: 'https://nominis.cef.fr/contenus/saint/8965/Les-saints-Innocents.html' },
      '12-29': { name: 'Saint Thomas Becket', link: 'https://nominis.cef.fr/contenus/saint/1730/Saint-Thomas-Becket.html' },
      '12-31': { name: 'Saint Sylvestre Ier', link: 'https://nominis.cef.fr/contenus/saint/1731/Saint-Sylvestre-I.html' }
    };
    
    return saintsCalendar;
  }

  // Données de fallback pour les saints avec liens fiables
  static getFallbackSaint() {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateKey = `${month}-${day}`;
    
    const saintsDatabase = this.getSaintsDatabase();
    const todaysSaint = saintsDatabase[dateKey];
    
    if (todaysSaint) {
      // Utiliser l'URL Nominis générée automatiquement au bon format
      const nominisUrl = todaysSaint.link || this.generateNominisUrl(today);
      
      return {
        date: today.toISOString().split('T')[0],
        saints: [{
          name: todaysSaint.name,
          description: todaysSaint.name,
          rank: 'Saint',
          link: nominisUrl,
          additionalLinks: [
            {
              title: 'Vie des Saints (Nominis)',
              url: nominisUrl
            },
            {
              title: 'Saints et Saintes (Vatican)',
              url: `https://www.vatican.va/news_services/liturgy/saints/ns_lit_doc_${today.getFullYear()}${month}${day}_${todaysSaint.name.toLowerCase().replace(/\s+/g, '_')}.html`
            },
            {
              title: 'Histoire complète (Magnificat)',
              url: `https://french.magnificat.net/saints/${todaysSaint.name.toLowerCase().replace(/\s+/g, '-')}`
            }
          ],
          source: 'Base de données locale (Calendrier romain)'
        }],
        mainSaint: {
          name: todaysSaint.name,
          description: todaysSaint.name,
          rank: 'Saint',
          link: nominisUrl,
          source: 'Calendrier romain officiel'
        }
      };
    }
    
    // Fallback pour dates non trouvées - saints populaires
    const fallbackSaints = [
      {
        name: 'Saints Louis et Zélie Martin',
        description: 'Parents de sainte Thérèse de Lisieux, premiers époux canonisés ensemble. Modèle de couple chrétien et de parents saints.',
        link: 'https://nominis.cef.fr/contenus/saint/8942/Saints-Louis-et-Zelie-Martin.html',
        patronage: 'Couples mariés et familles chrétiennes'
      },
      {
        name: 'Saint Joseph',
        description: 'Époux de la Vierge Marie, père adoptif de Jésus. Gardien de la Sainte Famille et protecteur des familles.',
        link: 'https://nominis.cef.fr/contenus/saint/250/Saint-Joseph.html',
        patronage: 'Pères de famille, époux, travailleurs'
      },
      {
        name: 'Sainte Anne et Saint Joachim',
        description: 'Parents de la Vierge Marie, grands-parents de Jésus. Modèles des grands-parents chrétiens.',
        link: 'https://nominis.cef.fr/contenus/saint/67/Sainte-Anne.html',
        patronage: 'Grands-parents et familles'
      }
    ];
    
    const randomSaint = fallbackSaints[Math.floor(Math.random() * fallbackSaints.length)];
    
    return {
      date: today.toISOString().split('T')[0],
      saints: [randomSaint],
      mainSaint: randomSaint,
      source: 'Fallback - Saints populaires pour familles'
    };
  }

  // Récupérer les prières populaires
  static getPrayers() {
    return [
      {
        id: 1,
        title: "Ave Maria",
        text: "Je vous salue, Marie, pleine de grâce, le Seigneur est avec vous. Vous êtes bénie entre toutes les femmes, et Jésus, le fruit de vos entrailles, est béni. Sainte Marie, Mère de Dieu, priez pour nous, pauvres pécheurs, maintenant et à l'heure de notre mort. Amen.",
        category: "Prières mariales"
      },
      {
        id: 2,
        title: "Notre Père",
        text: "Notre Père qui es aux cieux, que ton nom soit sanctifié, que ton règne vienne, que ta volonté soit faite sur la terre comme au ciel. Donne-nous aujourd'hui notre pain de ce jour. Pardonne-nous nos offenses, comme nous pardonnons aussi à ceux qui nous ont offensés. Et ne nous laisse pas entrer en tentation, mais délivre-nous du Mal. Amen.",
        category: "Prières fondamentales"
      },
      {
        id: 3,
        title: "Gloire au Père",
        text: "Gloire au Père, et au Fils, et au Saint-Esprit. Comme il était au commencement, maintenant et toujours, pour les siècles des siècles. Amen.",
        category: "Prières trinitaires"
      }
    ];
  }

  // Récupérer les neuvaines disponibles
  static getNovenasForDownload() {
    return [
      {
        id: 1,
        title: "Neuvaine à Notre-Dame de Lourdes",
        description: "Neuvaine de préparation à la fête de Notre-Dame de Lourdes (11 février)",
        downloadUrl: "https://lille.catholique.fr/app/uploads/sites/56/2025/01/Neuvaine-%C3%A0-ND-Lourdes.pdf",
        duration: "9 jours"
      },
      {
        id: 2,
        title: "Neuvaine au Sacré-Cœur de Jésus",
        description: "Neuvaine de dévotion au Sacré-Cœur, source d'amour et de miséricorde",
        downloadUrl: "https://www.regnumchristi.fr/wp-content/uploads/2023/06/Neuvaine-au-Sacre-Coeur-2023.pdf",
        duration: "9 jours"
      },
      {
        id: 3,
        title: "Neuvaine à Saint Joseph",
        description: "Neuvaine à saint Joseph, gardien de la Sainte Famille",
        downloadUrl: "https://rennes.catholique.fr/wp-content/uploads/sites/11/2024/03/neuvaine-a-saint-joseph.pdf",
        duration: "9 jours"
      }
    ];
  }
}

export default CatholicApiService;